import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export default function Terms() {
  return (
    <PageLayout>
      <Card className="max-w-4xl mx-auto">
        <CardHeader>
          <CardTitle className="text-3xl">Terms & Conditions</CardTitle>
          <p className="text-sm text-muted-foreground">Last updated: {new Date().toLocaleDateString()}</p>
        </CardHeader>
        <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-6">
          <section>
            <h2 className="text-xl font-semibold mb-3">1. Acceptance of Terms</h2>
            <p>
              By accessing and using CalcLab, you accept and agree to be bound by the terms and provision of this agreement. 
              If you do not agree to these terms, please do not use our service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">2. Service Description</h2>
            <p>
              CalcLab provides scientific calculators, learning resources, and AI-powered tools for physics, chemistry, finance, and mathematics. We offer both free and paid services:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Free Access:</strong> All standard calculators and tools, favorites feature, and 1 free SuperCalc query</li>
              <li><strong>Student & Educator Membership ($44/year):</strong> 100 SuperCalc tokens per month, full access to LaTeX Learning Center and future learning pathways, lesson plan generator + all free features</li>
              <li><strong>Standard Membership ($88/year):</strong> 200 SuperCalc tokens per month, full access to LaTeX Learning Center and future learning pathways, priority support + all features</li>
              <li><strong>Token Packs:</strong> Additional tokens can be purchased separately and are valid for 12 months from purchase</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">3. SuperCalc Pricing Transparency</h2>
            <p>
              SuperCalc is a paid feature because it uses advanced AI models that incur real costs for each query. 
              We charge only to cover operational expenses and maintain service quality. This is not a profit maximization strategy. 
              Our pricing is designed to be fair and sustainable. Monthly token allocations are designed to cover typical usage; 
              heavy users may need to purchase additional token packs.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">4. Free Trial</h2>
            <p>
              All registered users receive 1 free SuperCalc query to test the service before committing to a paid subscription. 
              This trial is provided once per account and cannot be reset or transferred.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">5. Token and Credit System</h2>
            <p>
              SuperCalc operates on a token-based credit system. By using our service, you acknowledge and agree to the following:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Virtual Currency:</strong> Tokens are a virtual good with no real-world monetary value. They cannot be redeemed for cash, traded, sold, or converted to any other form of value under any circumstances.</li>
              <li><strong>Non-Refundable:</strong> Tokens and credits are non-refundable once purchased or allocated. Unused tokens from token packs expire 12 months from the date of purchase.</li>
              <li><strong>Non-Transferable:</strong> Tokens and credits cannot be transferred between accounts, sold, exchanged, or converted to cash or any other form of value.</li>
              <li><strong>Monthly Allocation:</strong> Membership tokens are allocated monthly and do not roll over to subsequent months. Unused monthly tokens expire at the end of each billing period.</li>
              <li><strong>Purchased Tokens:</strong> Tokens purchased through token packs are valid for 12 months from the purchase date. After this period, any remaining tokens will expire and cannot be recovered.</li>
              <li><strong>Usage Deduction:</strong> Tokens are deducted from your balance when you use SuperCalc or document generation features. Different features may consume different amounts of tokens.</li>
              <li><strong>Balance Verification:</strong> It is your responsibility to monitor your token balance. We are not liable for any inconvenience caused by insufficient tokens.</li>
              <li><strong>System Changes:</strong> We reserve the right to modify the token system, pricing, allocation amounts, and expiry periods at any time with reasonable notice to users.</li>
              <li><strong>No Guarantee of Availability:</strong> We do not guarantee uninterrupted service availability. Service may be suspended for maintenance, upgrades, or unforeseen circumstances.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">6. Refund Policy</h2>
            <p>
              We offer a 7-day money-back guarantee on membership subscriptions with the following conditions:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Refund requests must be submitted within 7 days of membership purchase</li>
              <li>The refund amount will be the full subscription price minus the actual cost of any SuperCalc queries used during the period</li>
              <li>SuperCalc usage costs will be calculated based on our operational costs (AI model tokens consumed)</li>
              <li>Refunds are processed to the original payment method within 5-10 business days</li>
              <li><strong>Token Pack Purchases:</strong> Token pack purchases are final and non-refundable. No refunds will be issued for token packs under any circumstances.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">7. Account Termination and Credits</h2>
            <p>
              Upon termination of your account, whether voluntary or involuntary:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>All unused tokens and credits will be forfeited immediately</li>
              <li>No refunds or compensation will be provided for unused tokens</li>
              <li>You will lose access to any premium features associated with your membership</li>
              <li>We are not obligated to provide any notice before forfeiture of credits upon account termination for violation of these terms</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">8. User Accounts</h2>
            <p>
              You are responsible for maintaining the confidentiality of your account and password. 
              You agree to accept responsibility for all activities that occur under your account.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">9. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the service for any unlawful purpose</li>
              <li>Attempt to gain unauthorized access to any portion of the service</li>
              <li>Abuse, harass, or harm other users</li>
              <li>Share your account credentials with others</li>
              <li>Use automated systems to access the service without permission</li>
              <li>Attempt to manipulate, exploit, or circumvent the token system</li>
              <li>Create multiple accounts to abuse free trials or promotional offers</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">10. Intellectual Property</h2>
            <p>
              All content, features, and functionality of CalcLab are owned by us and are protected by international 
              copyright, trademark, and other intellectual property laws.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">11. Limitation of Liability</h2>
            <p>
              CalcLab is provided "as is" without warranties of any kind, express or implied. We shall not be liable for any indirect, 
              incidental, special, consequential, or punitive damages resulting from your use or inability to use the service. 
              The calculations and solutions provided are for educational purposes only and should be verified independently 
              for critical applications. We are not liable for any loss of tokens or credits due to technical issues, 
              account issues, or any other circumstances. Our total liability to you for any claims arising from or related to 
              this agreement shall not exceed the amount you paid us in the 12 months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">12. Force Majeure</h2>
            <p>
              We shall not be liable for any failure or delay in performing our obligations where such failure or delay 
              results from circumstances beyond our reasonable control, including but not limited to: acts of God, 
              natural disasters, war, terrorism, riots, embargoes, acts of civil or military authorities, fire, floods, 
              accidents, pandemic, strikes, or shortages of transportation, facilities, fuel, energy, labor, or materials.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">13. Dispute Resolution and Arbitration</h2>
            <p>
              Any dispute, controversy, or claim arising out of or relating to these Terms, or the breach thereof, 
              shall be settled by binding arbitration in accordance with applicable arbitration rules. The arbitration 
              shall be conducted in English. The decision of the arbitrator shall be final and binding upon both parties.
            </p>
            <p className="mt-2">
              <strong>Class Action Waiver:</strong> You agree that any dispute resolution proceedings will be conducted only 
              on an individual basis and not in a class, consolidated, or representative action. You waive any right to 
              participate in class action lawsuits or class-wide arbitration against CalcLab.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">14. Termination</h2>
            <p>
              We reserve the right to terminate or suspend your account at our sole discretion, without notice, 
              for conduct that we believe violates these Terms or is harmful to other users, us, or third parties. 
              This includes, but is not limited to, abuse of the token system or violation of acceptable use policies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">15. Changes to Terms</h2>
            <p>
              We reserve the right to modify these terms at any time. We will notify users of any material changes 
              via email or through the service. Continued use after changes constitutes acceptance of the new terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">16. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with applicable laws, 
              without regard to its conflict of law provisions.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">17. Severability</h2>
            <p>
              If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited 
              or eliminated to the minimum extent necessary so that these Terms shall otherwise remain in full force 
              and effect and enforceable.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">18. Contact</h2>
            <p>
              For questions about these Terms, please contact us through our support page (available to paid members).
            </p>
          </section>
        </CardContent>
      </Card>
    </PageLayout>
  );
}